import { memo, useContext, useEffect, useMemo, useState } from 'react';
import { FieldArray, Form, Formik, useField, useFormikContext } from 'formik';
import { nanoid } from 'nanoid';
import * as yup from 'yup';
import { Button, Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap';
import _ from 'lodash';
import { useParams } from 'react-router';
import styled, { css } from 'styled-components';
import {
  ControlledDeleteButton,
  OptionalSection,
  SubmitButton,
  ValidatedInput,
} from './templates/template-components';
import { DISTANCE_UNITS } from '../constants/units';
import CropSelector from './CropSelector';
import { MapContext } from '../contexts/MapContext';
import DraggableModal from './basics/DraggableModal';
import { UNKNOWN_CROP } from '../constants/fields';
import { colorForCrop, cropThemes } from '../utils/colors';
import UnsavedFormPrompt from './basics/UnsavedFormPrompt';
import DirtyPatternModal from './DirtyPatternModal';
import { useUnits } from '../contexts/UnitContext';
import { PatternEditorContext } from '../contexts/PatternEditorContext';
import { useToggleState } from '../utils/hooks';
import { getFirstMutationResult } from '../utils/graphql';
import { uniqueName } from '../utils/utils';
import { captureEvent } from '../utils/analytics';
import { getTotalPatternFt } from '../utils/patterns';
import CropVarietySelector from './CropVarietySelector';
import CropVarietyIcon from './icons/CropVarietyIcon';
import { CropVarietyDropdown } from './right-menu/menu-components';
import IconContainer from './basics/IconContainer';
import useMutateWithState from '../hooks/useMutateWithState';

// If the input is disabled, style it as normal text
const ValidatedInputWithDisabledStyle = styled(ValidatedInput)`
  &:disabled {
    color: inherit;
    background-color: transparent;
    border: none;
    padding: 0;
  }
`;

// If the button is disabled, style it normally
const DropdownToggleWithDisabledStyle = styled(DropdownToggle)`
  &:disabled {
    opacity: 1;
  }
`;

const SpacingText = styled.div`
  height: 20px;
  font-size: 10px;
  max-width: 50px;
`;

const TinyValidatedInput = styled(ValidatedInputWithDisabledStyle)`
  width: 50px;
  min-width: 50px;
  padding-left: 3px;
  height: 20px;
  font-size: 10px;
`;

const InlineCropSelector = styled(CropSelector)`
  .rbt-input-main {
    margin: 0 24px;
  }
  .rbt-menu.dropdown-menu {
    position: static !important;
    width: 100%;
    transform: none !important;
    border: none;
    overflow: scroll;
    max-height: 250px !important;
  }
`;

const SequenceConnectorLine = styled.div`
  min-width: 50px;
  height: 2px;
  background-color: var(--overyield-blue);
`;

const SequenceText = styled.div`
  color: var(--overyield-blue);
  height: 29px;
  padding-top: 12px;
`;

const cropCircleStyles = css`
  min-width: 15px;
  min-height: 15px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
`;

const CropCircle = styled.div`
  background-color: ${({ crop }) => colorForCrop(crop, cropThemes.LIGHT)};
  border: 1px solid
    ${({ crop, invalid }) =>
      invalid ? 'red' : colorForCrop(crop, cropThemes.DARK)};
  ${cropCircleStyles}
`;

const CropCircleButton = styled(Button)`
  padding: 0;
  font-size: 12px;
  line-height: 12px;
  font-weight: bold;
  ${cropCircleStyles}
`;

const CropSelectorDropdownMenu = styled(DropdownMenu)`
  max-height: 300px;
  font-size: inherit;
  ${({ $loading }) => ($loading ? 'display: none !important;' : '')}
`;

const toFormValues = (values) => ({
  ...values,
  cropSequence: values.cropSequence.map((item) => ({
    ...item,
    tempId: nanoid(),
  })),
});

const patternSchema = yup.object().shape({
  name: yup.string().required('Required'),
  description: yup.string().nullable(),
  cropSequence: yup
    .array()
    .of(
      yup.object().shape({
        crop: yup
          .object()
          .shape({
            id: yup.string().required('Required'),
          })
          .required('Required'),
        spacingFt: yup
          .number()
          .positive('Must be positive')
          .required('Required'),
      }),
    )
    .test(
      'min-length',
      'At least two crops are required',
      (sequence) => sequence.length >= 2,
    ),
});

const CropSelectorField = ({ name, onChange = _.noop, onRemove, ...props }) => {
  const {
    crops: { availableCrops: cropMap },
  } = useContext(MapContext);
  const [, meta, helpers] = useField(name);
  const { value, touched, error } = meta;
  const { setValue } = helpers;
  const selectedCrop = cropMap[value?.crop?.id];
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const toggle = () => setIsOpen(!isOpen);

  return (
    <Dropdown isOpen={isOpen || loading} toggle={toggle}>
      <DropdownToggleWithDisabledStyle
        size="sm"
        className="p-0"
        color="link"
        {...props}
      >
        <CropCircle
          crop={selectedCrop || UNKNOWN_CROP}
          invalid={touched && error}
          {...props}
        />
      </DropdownToggleWithDisabledStyle>
      <CropSelectorDropdownMenu
        $loading={loading}
        container="body"
        style={{ zIndex: 1050 }}
        modifiers={{ preventOverflow: { boundariesElement: 'viewport' } }}
      >
        {onRemove && (
          <div className="d-flex w-100 justify-content-end">
            <Button
              type="button"
              onClick={onRemove}
              className="font-tiny py-0 text-muted"
              color="link"
            >
              × Remove
            </Button>
          </div>
        )}
        <InlineCropSelector
          // Don't show "Type to add crop" message; handled by placeholder
          newSelectionPrompt={undefined}
          onLoading={setLoading}
          placeholder="Search or add crop..."
          selectedCrop={selectedCrop}
          onCropSelect={(crop) => {
            setValue({ ...value, crop, cropVariety: null });
            onChange(crop);
            setIsOpen(false);
          }}
          open
        />
      </CropSelectorDropdownMenu>
    </Dropdown>
  );
};

const CropVarietyLabel = styled.div`
  margin-left: 31px; // 15px for icon + 16px for margin on CropCircle
  display: flex;
  align-items: center;
`;

const CropPatternList = styled(({ className, readonly }) => {
  const { values, setValues } = useFormikContext();
  const {
    crops: { availableCrops: cropMap },
  } = useContext(MapContext);

  const [varietiesIsOpen, toggleVarietiesOpen] = useToggleState();

  const uniqueCrops = _.uniqBy(
    values.cropSequence.map(({ crop, cropVariety }) => ({
      crop: cropMap[crop?.id] || UNKNOWN_CROP,
      cropVariety,
    })),
    'crop.id',
  );
  const cropSelected = uniqueCrops.some(({ crop }) => crop.id != null);

  return (
    <div className={className}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        Crops
        {cropSelected && (
          <CropVarietyDropdown
            isOpen={varietiesIsOpen}
            onClick={toggleVarietiesOpen}
            plural
          />
        )}
      </div>
      <ul>
        {uniqueCrops.map(({ crop, cropVariety }) => (
          <li key={crop.id || crop.name} className="my-1">
            <div className="d-flex align-items-center">
              <CropCircle crop={crop} className="mr-3" /> {crop.name}
            </div>
            {varietiesIsOpen && crop.id != null && (
              <CropVarietyLabel className="mt-1">
                <IconContainer className="mr-2">
                  <CropVarietyIcon />
                </IconContainer>
                {readonly ? (
                  cropVariety?.name || 'None'
                ) : (
                  <CropVarietySelector
                    id={`crop-${crop.id}-crop-variety`}
                    key={crop.id}
                    cropId={crop.id}
                    cropVarietyId={cropVariety?.id}
                    onCropVarietySelect={(selectedCropVariety) => {
                      captureEvent('crop-variety-select', {
                        action: selectedCropVariety ? 'assign' : 'remove',
                        location: 'pattern',
                      });
                      setValues({
                        ...values,
                        cropSequence: values.cropSequence.map((item) =>
                          // Update crop variety for all items with this crop
                          item.crop?.id === crop.id
                            ? { ...item, cropVariety: selectedCropVariety }
                            : item,
                        ),
                      });
                    }}
                  />
                )}
              </CropVarietyLabel>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
})`
  border-top: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
  margin-left: -1rem;
  margin-right: -1rem;
  padding: 1rem;
`;

const CropSequenceItem = memo(({ item, onRemove, readonly, name }) => {
  const { displayUnit, transformToUnit } = useUnits(DISTANCE_UNITS);

  return (
    <>
      <div className="position-relative">
        <CropSelectorField
          name={name}
          disabled={readonly}
          onRemove={onRemove}
        />
      </div>
      <div className="d-flex flex-column align-items-center">
        <SequenceText>↔</SequenceText>
        <SequenceConnectorLine className="mb-2" />
        {readonly ? (
          <SpacingText>
            {transformToUnit(item.spacingFt)} {displayUnit}
          </SpacingText>
        ) : (
          <TinyValidatedInput
            type="number"
            name={`${name}.spacingFt`}
            min={0}
            step="any"
            unitSpec={DISTANCE_UNITS}
            formGroupClassName="mb-0"
          >
            {({ renderInput }) => renderInput()}
          </TinyValidatedInput>
        )}
      </div>
    </>
  );
});

CropSequenceItem.displayName = 'CropSequenceItem';

const CropSequence = ({ readonly }) => {
  const { values } = useFormikContext();

  return (
    <FieldArray name="cropSequence">
      {({ push, remove }) => {
        const lastCropSequenceItem =
          values.cropSequence[values.cropSequence.length - 1];

        const onRemoveItem = (index) =>
          values.cropSequence.length > 2 && // Don't allow fewer than 2 items
          !readonly &&
          (() => remove(index));

        return (
          <div className="d-flex align-items-center overflow-auto pb-4 mb-1 mx-n3 px-3">
            {values.cropSequence.map((item, index) => (
              <CropSequenceItem
                key={item.tempId}
                item={item}
                onRemove={onRemoveItem(index)}
                name={`cropSequence.${index}`}
                readonly={readonly}
              />
            ))}
            {!readonly && (
              <CropCircleButton
                onClick={() =>
                  push({
                    ...lastCropSequenceItem,
                    tempId: nanoid(),
                  })
                }
              >
                ＋
              </CropCircleButton>
            )}
          </div>
        );
      }}
    </FieldArray>
  );
};

const Footer = ({
  readonly,
  onDuplicateAndEdit,
  onDelete,
  submitButtonText,
  closePanel,
}) => {
  const [deleting, setDeleting] = useState(false);
  return (
    <div className="d-flex justify-content-between">
      <div>
        {onDelete && (
          <ControlledDeleteButton
            size="sm"
            onDelete={onDelete}
            deleting={deleting}
            setDeleting={setDeleting}
          >
            Delete
          </ControlledDeleteButton>
        )}
      </div>
      {!deleting && (
        <div>
          {onDuplicateAndEdit && (
            <Button size="sm" className="mr-2" onClick={onDuplicateAndEdit}>
              Duplicate & Edit
            </Button>
          )}
          {readonly ? (
            <Button size="sm" onClick={closePanel}>
              Done
            </Button>
          ) : (
            <SubmitButton size="sm">{submitButtonText}</SubmitButton>
          )}
        </div>
      )}
    </div>
  );
};

const CropPatternEditor = ({ readonly }) => {
  const { farmId } = useParams();
  const {
    editorPattern: initialPattern,
    setEditorPattern,
    doOrConfirmIfDirty,
  } = useContext(PatternEditorContext);
  const {
    patterns: {
      updateInRowPattern,
      createInRowPattern,
      removeInRowPattern,
      inRowPatterns,
    },
  } = useContext(MapContext);
  const [, mutateAndSetState, resetMutationState] = useMutateWithState();

  useEffect(() => {
    resetMutationState();
  }, [initialPattern]); // eslint-disable-line react-hooks/exhaustive-deps

  const initialValues = useMemo(
    () => (initialPattern ? toFormValues(initialPattern) : null),
    [initialPattern],
  );

  const { displayUnit, transformToUnit } = useUnits(DISTANCE_UNITS);

  const closePanel = () => doOrConfirmIfDirty(() => setEditorPattern(null));

  if (!initialPattern) return null;

  const duplicateAndEdit =
    initialPattern.id && !readonly
      ? () => {
          captureEvent('pattern-clicked-duplicate');
          setEditorPattern({
            description: initialPattern.description,
            cropSequence: initialPattern.cropSequence,
            name: uniqueName(initialPattern.name, inRowPatterns),
          });
        }
      : null;

  const deletePattern =
    initialPattern.id && !readonly
      ? () => {
          captureEvent('pattern-deleted');
          mutateAndSetState(() => removeInRowPattern(initialPattern.id))
            .then((response) => {
              const result = getFirstMutationResult(response?.data);
              if (result) {
                setEditorPattern(null);
              }
            })
            .catch(() => {
              // Do nothing
            });
        }
      : null;

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={patternSchema}
      onSubmit={(values, { setSubmitting }) => {
        mutateAndSetState(() =>
          initialPattern.id
            ? updateInRowPattern({
                ...values,
                __typename: undefined,
                cropSequence: undefined,
              })
            : createInRowPattern(farmId, {
                ...values,
                cropSequence: values.cropSequence.map((item) => ({
                  crop: item.crop,
                  cropVariety: item.cropVariety,
                  spacingFt: item.spacingFt,
                })),
              }),
        )
          .then((response) => {
            const result = getFirstMutationResult(response?.data);
            setSubmitting(false);
            if (result) {
              captureEvent(
                initialPattern.id
                  ? 'pattern-saved-existing'
                  : 'pattern-saved-new',
              );
              setEditorPattern(null);
            }
          })
          .catch(() => {
            setSubmitting(false);
          });
      }}
    >
      {({ values }) => (
        <Form>
          <DirtyPatternModal />
          <UnsavedFormPrompt />
          <DraggableModal
            startPosition={{ top: 60, right: 266 }}
            height={300}
            title={
              <ValidatedInputWithDisabledStyle
                name="name"
                placeholder="Pattern name"
                disabled={readonly}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {({ renderInput }) => renderInput()}
              </ValidatedInputWithDisabledStyle>
            }
            footerContent={
              <Footer
                readonly={readonly}
                submitButtonText={initialPattern.id ? 'Save' : 'Create'}
                closePanel={closePanel}
                onDuplicateAndEdit={duplicateAndEdit}
                onDelete={deletePattern}
              />
            }
            isOpen
            setIsOpen={closePanel}
          >
            <div className="p-3">
              <div>
                Sequence ({transformToUnit(getTotalPatternFt(values))}{' '}
                {displayUnit})
              </div>
              <CropSequence readonly={!!initialPattern.id || readonly} />
              <CropPatternList
                readonly={!!initialPattern.id || readonly}
                className="mb-2"
              />
              <OptionalSection
                forceShown={!!values.description}
                buttonContent="Add description"
                sectionKey="crop-pattern-description"
              >
                <ValidatedInputWithDisabledStyle
                  type="textarea"
                  name="description"
                  label="Description"
                  rows="3"
                  disabled={readonly}
                  formGroupClassName="mb-0"
                />
              </OptionalSection>
            </div>
          </DraggableModal>
        </Form>
      )}
    </Formik>
  );
};

export default CropPatternEditor;
